import { UserEntity } from '@data';
import React, { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  useSidebar,
} from '@ui/components/ui/sidebar';
import ProfilePopover from './profilePopover';
import { NavItems } from './sidebar-components';
import panencoLogo from './panenco-logo.png';

export interface SidebarProps {
  user: UserEntity;
}

const AppSidebar = ({ user }: SidebarProps): ReactElement => {
  const { t } = useTranslation();
  const { setOpenMobile } = useSidebar();
  const { pathname } = useLocation();

  // Close the mobile offcanvas Sheet on navigation; on desktop openMobile is unused, so this is a no-op.
  React.useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  return (
    // Own view-transition-name so the sidebar is excluded from the sliding page root during
    // list↔detail transitions and stays put while the content swipes.
    <Sidebar collapsible="offcanvas" style={{ viewTransitionName: 'app-sidebar' }}>
      {/* pb-0: the logo went down a size, and the space it used to need was left sitting above the nav. */}
      <SidebarHeader className="pb-0">
        <Link className="flex items-center px-2 py-0.5" to="/" aria-label={t('sidebar.appName')}>
          {/* 28px, which puts the wordmark within a pixel of the one on panenco.com. */}
          <img className="h-7 w-auto max-w-[140px] object-contain" src={panencoLogo} alt={t('sidebar.appName')} />
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent role="navigation" aria-label={t('a11y.mainNavigation')}>
            <NavItems user={user} />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <ProfilePopover user={user} />
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
